import { useEffect, useRef } from 'react';

export default function GalaxyBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let stars = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = document.documentElement.scrollHeight;
    };

    const createStars = () => {
      stars = [];
      const count = Math.floor((canvas.width * canvas.height) / 10000);
      for (let i = 0; i < count; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: Math.random() * 1.8 + 0.2,
          opacity: Math.random() * 0.6 + 0.15,
          twinkleSpeed: Math.random() * 0.01 + 0.003,
          twinkleOffset: Math.random() * Math.PI * 2,
          color: ['220,210,255', '200,220,255', '255,210,230', '255,230,210'][Math.floor(Math.random() * 4)],
        });
      }
    };

    // Smooth nebula cloud - no dots, pure gradients
    const drawCloud = (cx, cy, rx, ry, color, opacity, rotation) => {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rotation);
      const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, Math.max(rx, ry));
      grad.addColorStop(0, `rgba(${color}, ${opacity})`);
      grad.addColorStop(0.2, `rgba(${color}, ${opacity * 0.75})`);
      grad.addColorStop(0.45, `rgba(${color}, ${opacity * 0.35})`);
      grad.addColorStop(0.7, `rgba(${color}, ${opacity * 0.1})`);
      grad.addColorStop(1, `rgba(${color}, 0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    // Flowing arm made of overlapping soft ellipses
    const drawSoftArm = (cx, cy, angle, length, width, color, opacity) => {
      const steps = 8;
      for (let i = 0; i < steps; i++) {
        const t = i / steps;
        const curveAngle = angle + t * Math.PI * 1.2;
        const dist = t * length;
        const x = cx + Math.cos(curveAngle) * dist;
        const y = cy + Math.sin(curveAngle) * dist;
        const size = width * (1 - t * 0.6);
        const a = opacity * (1 - t * 0.7);
        drawCloud(x, y, size * 1.3, size * 0.7, color, a, curveAngle + 0.5);
      }
    };

    const drawGalaxy = (cx, cy, size, tilt, baseOpacity, time) => {
      const slowTilt = tilt + time * 0.02;

      // Large diffuse outer halo
      drawCloud(cx, cy, size * 1.0, size * 0.7, '130, 100, 200', baseOpacity * 0.15, slowTilt);

      // Main pink/magenta nebula mass
      drawCloud(cx - size * 0.1, cy + size * 0.05, size * 0.55, size * 0.35, '210, 90, 170', baseOpacity * 0.5, slowTilt + 0.6);

      // Blue nebula mass
      drawCloud(cx + size * 0.15, cy - size * 0.1, size * 0.5, size * 0.3, '90, 140, 230', baseOpacity * 0.4, slowTilt - 0.4);

      // Purple-violet mass
      drawCloud(cx - size * 0.15, cy - size * 0.12, size * 0.4, size * 0.28, '170, 120, 220', baseOpacity * 0.45, slowTilt + 1.5);

      // Teal accent
      drawCloud(cx + size * 0.2, cy + size * 0.2, size * 0.3, size * 0.18, '100, 190, 210', baseOpacity * 0.25, slowTilt + 2.5);

      // Soft flowing arms (smooth overlapping clouds, not dots)
      drawSoftArm(cx, cy, slowTilt, size * 0.7, size * 0.15, '190, 140, 240', baseOpacity * 0.35);
      drawSoftArm(cx, cy, slowTilt + Math.PI * 0.7, size * 0.65, size * 0.13, '200, 100, 180', baseOpacity * 0.3);
      drawSoftArm(cx, cy, slowTilt + Math.PI * 1.4, size * 0.6, size * 0.12, '120, 150, 230', baseOpacity * 0.28);

      // Bright core - warm glow
      drawCloud(cx, cy, size * 0.22, size * 0.15, '220, 200, 255', baseOpacity * 0.9, slowTilt);
      drawCloud(cx, cy, size * 0.13, size * 0.09, '240, 225, 255', baseOpacity, slowTilt);
      drawCloud(cx, cy, size * 0.07, size * 0.04, '255, 245, 240', baseOpacity * 0.7, slowTilt);

      // Scattered outer wisps
      drawCloud(cx - size * 0.45, cy + size * 0.35, size * 0.25, size * 0.12, '190, 110, 180', baseOpacity * 0.2, slowTilt + 3);
      drawCloud(cx + size * 0.4, cy - size * 0.4, size * 0.2, size * 0.1, '140, 170, 240', baseOpacity * 0.18, slowTilt - 2);
    };

    let time = 0;
    const draw = () => {
      time += 0.008;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const baseSize = Math.min(canvas.width, 1600);

      // Galaxy 1 - top right
      drawGalaxy(canvas.width * 0.8, 200, baseSize * 0.4, 0.3, 0.45, time);

      // Galaxy 2 - left center
      drawGalaxy(canvas.width * 0.08, canvas.height * 0.5, baseSize * 0.35, 1.2, 0.38, time * 0.8);

      // Galaxy 3 - bottom right
      if (canvas.height > 700) {
        drawGalaxy(canvas.width * 0.6, canvas.height * 0.88, baseSize * 0.3, 2.5, 0.3, time * 0.6);
      }

      // Twinkling stars
      stars.forEach(star => {
        const twinkle = Math.sin(time * star.twinkleSpeed * 40 + star.twinkleOffset) * 0.5 + 0.5;
        ctx.globalAlpha = star.opacity * twinkle;
        ctx.fillStyle = `rgba(${star.color})`;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
        ctx.fill();

        if (star.r > 1.3 && twinkle > 0.65) {
          ctx.globalAlpha = star.opacity * twinkle * 0.25;
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.r * 3.5, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      ctx.globalAlpha = 1;

      animationId = requestAnimationFrame(draw);
    };

    resize();
    createStars();
    draw();

    const handleResize = () => { resize(); createStars(); };
    window.addEventListener('resize', handleResize);

    const observer = new ResizeObserver(() => {
      const newHeight = document.documentElement.scrollHeight;
      if (Math.abs(canvas.height - newHeight) > 100) {
        canvas.height = newHeight;
        createStars();
      }
    });
    observer.observe(document.body);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
      data-testid="galaxy-background"
    />
  );
}
